import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ScreenSplitMode } from 'shared-types/ScreenSplit';
import { Playlist } from 'shared-types/playlist';

import { sessionInitialState } from './constants';
import { BamSessionMode, BamSessionUser, QUARTER_HOURS_COUNTS } from './types';

const sessionSlice = createSlice({
  name: 'session',
  initialState: sessionInitialState,
  reducers: {
    setSessionEndTimestamp: (state, action: PayloadAction<number>) => {
      state.endTimestamp = action.payload;
    },
    endCurrentSession: (state) => {
      state.endTimestamp = null;
    },
    setActivatedVolumeState: (state, action: PayloadAction<boolean>) => {
      state.activatedVolume = action.payload;
    },
    setScreenSplitMode: (state, action: PayloadAction<ScreenSplitMode>) => {
      state.screenSplitMode = action.payload;
    },
    setShouldSkipStaffPassword: (state, action: PayloadAction<boolean>) => {
      state.shouldSkipStaffPassword = action.payload;
    },
    setIsStorageActivated: (state, action: PayloadAction<boolean>) => {
      state.isStorageActivated = action.payload;
    },
    setBamSessionMode: (state, action: PayloadAction<BamSessionMode>) => {
      state.bamSessionMode = action.payload;
    },
    setCustomPlayLists: (state, action: PayloadAction<Array<Playlist> | null>) => {
      state.customPlaylists = action.payload?.sort((a, b) => a.order - b.order) ?? null;
    },
    setBamSessionUser: (state, action: PayloadAction<BamSessionUser>) => {
      state.bamSessionUser = action.payload;
    },
    setBamSessionUserSessionStarted: (state, action: PayloadAction<boolean>) => {
      state.bamSessionUser.sessionStarted = action.payload;
    },
    resetBamSessionUser: (state) => {
      state.bamSessionUser = {
        clientCode: null,
        lastName: null,
        firstName: null,
        bamCustomerId: null,
        email: null,
        sessionStarted: false,
      };
    },
    setBamProlongationPossibility: (
      state,
      action: PayloadAction<{ count: QUARTER_HOURS_COUNTS; isProlongationPossible: boolean }>,
    ) => {
      state.bamProlongation.isProlongationPossiblePerTimeBlock[action.payload.count] =
        action.payload.isProlongationPossible;
    },
    setBamProlongationHourPrice: (state, action: PayloadAction<number>) => {
      state.bamProlongation.currentPricePerHour = action.payload;
    },
    setIsBamProlongationChanging: (state, action: PayloadAction<boolean>) => {
      state.bamProlongation.isProlongationStateWaitingForChange = action.payload;
    },
    resetBamProlongation: (state) => {
      state.bamProlongation = sessionInitialState.bamProlongation;
    },
    setRemainingSessionTimeInMilliseconds: (state, action: PayloadAction<number>) => {
      state.remainingSessionTimeInMilliseconds = action.payload;
    },
    setIsVideoRecording: (state, action: PayloadAction<boolean>) => {
      state.isVideoRecording = action.payload;
    },
  },
});

export const {
  setSessionEndTimestamp,
  setBamProlongationPossibility,
  endCurrentSession,
  setActivatedVolumeState,
  setScreenSplitMode,
  setShouldSkipStaffPassword,
  setIsStorageActivated,
  setBamSessionMode,
  setCustomPlayLists,
  setBamSessionUser,
  resetBamSessionUser,
  setRemainingSessionTimeInMilliseconds,
  setBamProlongationHourPrice,
  setIsBamProlongationChanging,
  setIsVideoRecording,
  resetBamProlongation,
  setBamSessionUserSessionStarted,
} = sessionSlice.actions;

export default sessionSlice.reducer;
